import { useState, useEffect } from "react";
import { Users, DollarSign, TrendingUp, Globe, Briefcase } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import { BackgroundPattern } from "./BackgroundPattern";

// Updated stats with focus on X.com growth
const stats = [
  {
    title: "digika.ai Growth",
    value: "100,000+",
    change: "+100,000% in 7 days",
    icon: (
      <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-emerald-500 to-sky-500 flex items-center justify-center shadow-lg group-hover:shadow-emerald-500/25 transition-all duration-300 rotate-3">
        <span className="text-white font-bold text-xl">D</span>
      </div>
    ),
    highlight: true,
  },
  {
    title: "Freelancers Hired",
    value: "10,000+",
    change: "+12%",
    icon: <Users className="w-8 h-8 text-emerald-500" />,
    highlight: true,
  },
  {
    title: "Projects Completed",
    value: "25,000+",
    change: "+18%",
    icon: <Briefcase className="w-8 h-8 text-emerald-500" />,
  },
  {
    title: "In Payments",
    value: "$250M+",
    change: "+25%",
    icon: <DollarSign className="w-8 h-8 text-emerald-500" />,
  },
  {
    title: "Client Satisfaction",
    value: "97%",
    change: "+15%",
    icon: <TrendingUp className="w-8 h-8 text-emerald-500" />,
  },
  {
    title: "Active Clients",
    value: "120+",
    change: "Countries",
    icon: <Globe className="w-8 h-8 text-emerald-500" />,
  },
];

export const CompanyStatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById("stats-section");
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  return (
    <section id="stats-section" className="relative py-24 overflow-hidden">
      {/* Enhanced background with multiple layers and images */}
      <BackgroundPattern />

      <div className="relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            What Sets Us Apart
          </h2>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              >
                <Card
                  className={`overflow-hidden border-none ${
                    stat.highlight
                      ? "bg-gradient-to-br from-sky-500/10 to-blue-600/10"
                      : "bg-white/80"
                  } backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}
                >
                  <div
                    className={`h-1 ${
                      stat.highlight
                        ? "bg-gradient-to-r from-sky-400 to-blue-600"
                        : "bg-gradient-to-r from-emerald-400 to-teal-500"
                    }`}
                  ></div>
                  <CardContent className="p-8">
                    <div className="flex items-center justify-between">
                      <div
                        className={`p-3 ${
                          stat.highlight
                            ? "bg-sky-50 text-sky-600"
                            : "bg-emerald-50"
                        } rounded-xl ring-2 ring-white/50 shadow-sm`}
                      >
                        {stat.icon}
                      </div>
                      <span
                        className={`text-sm font-medium px-3 py-1 rounded-full ${
                          stat.highlight
                            ? "bg-sky-100 text-sky-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {stat.change}
                      </span>
                    </div>
                    <div className="mt-6">
                      <p
                        className={`text-3xl font-bold mb-2 ${
                          stat.highlight ? "text-sky-950" : "text-gray-900"
                        }`}
                      >
                        {stat.value}
                      </p>
                      <p className="text-sm text-gray-600">{stat.title}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
